import flatten from 'lodash/flatten';
import { NotAvailable } from '@common/i18n/constants';
import {
  DataHistoryAggregation,
  GraphItemContextType,
  GraphItemVisualisationType,
  type GraphProfileContextType,
  type GraphProfileRequestGraph,
  type GraphProfileRequestGraphItem,
  type GraphProfilesRequest,
  type GraphProfilesResponse,
  type GraphProfilesResponseGraph,
  type GraphProfilesResponseGraphItem,
  type ResolutionUnit,
  type UserLevel,
} from '@common/types/api';
import { RegisterValueType } from '@common/types/models/Register';
import {
  emptySelectedRegisterDataSeriesGraphs,
  type RegisterDataSeries,
  type SelectedRegisterDataSeriesGraph,
} from '@common/types/models/SelectedDataPoints';
import { type BitMask16 } from '@common/utils/formatting/multiOptions';

interface DiagramItem {
  registerSpecificationId: number;
  registerSpecificationGroupId: number;
  registerIndex: number;
  registerName?: string;
  bitMask?: BitMask16;
  contextType: GraphItemContextType;
  visualisationType: GraphItemVisualisationType;
  resolutionUnits?: ResolutionUnit[];
  aggregationMethod?: DataHistoryAggregation;
}

interface Diagram {
  graphItems: DiagramItem[];
}

export interface GraphProfile {
  id: string;
  isGlobal: boolean;
  graphProfileContextType?: GraphProfileContextType;
  accessLevelId?: UserLevel;
  name: string;
  deviceTypeId: number;
  diagrams: Diagram[];
}

export const emptyGraphProfiles: GraphProfile[] = [];

export type PartialGraphProfile = Omit<GraphProfile, 'id' | 'isGlobal'>;

const diagramItemMatchesRegister = (
  diagramItem: DiagramItem,
  registerDataSeries: RegisterDataSeries,
) =>
  diagramItem.registerIndex === registerDataSeries.id
  && diagramItem.registerSpecificationId === registerDataSeries.registerId
  // No group ID set or matches Register
  && (!diagramItem.registerSpecificationGroupId
    || diagramItem.registerSpecificationGroupId
      === registerDataSeries.registerSpecificationGroupId)
  // can be both null and undefined
  && diagramItem.bitMask == registerDataSeries.valueBitMask;

// Find and set group ID if the profile was stored without one.
// This is needed to later properly detect changes in profiles.
export const graphProfileWithGroupId = (
  graphProfile: GraphProfile,
  registerDataSeriesArray: RegisterDataSeries[],
): GraphProfile => ({
  ...graphProfile,
  diagrams: graphProfile.diagrams.map((diagram): Diagram => ({
    ...diagram,
    graphItems: diagram.graphItems.map((diagramItem): DiagramItem => {
      // Check even if profile already has group ID
      const registerDataSeries = registerDataSeriesArray.find((register) =>
        diagramItemMatchesRegister(diagramItem, register)
      );
      return {
        ...diagramItem,
        registerSpecificationGroupId: registerDataSeries?.registerSpecificationGroupId ?? 0,
      };
    }),
  })),
});

/*
 * Map data points (registers) to diagrams functions
 */
const mapDataPoint = (registerDataSeries: RegisterDataSeries): DiagramItem => ({
  registerSpecificationId: registerDataSeries.registerId,
  registerSpecificationGroupId: registerDataSeries.registerSpecificationGroupId,
  registerIndex: +registerDataSeries.registerIndex,
  registerName: registerDataSeries.name,
  bitMask: registerDataSeries.valueBitMask,
  contextType: registerDataSeries.contextType ?? GraphItemContextType.DataPoints,
  visualisationType: registerDataSeries.visualisationType ?? GraphItemVisualisationType.Line,
  aggregationMethod: registerDataSeries.desiredAggregation,
});

const mapRegisterDataSeriesArrayToDiagram = (
  registerDataSeriesArray: RegisterDataSeries[],
): Diagram => ({
  graphItems: registerDataSeriesArray.map(mapDataPoint)
    .sort((a, b) => a.registerIndex - b.registerIndex),
});

export const mapSelectedRegisterDataSeriesGraphToGraphDiagrams = (
  graphs: SelectedRegisterDataSeriesGraph[],
) => (
  graphs.map((graph) => mapRegisterDataSeriesArrayToDiagram(graph.registerDataSeriesArray))
);

const createSyntheticRegisterDataSeries = (graphItem: DiagramItem): RegisterDataSeries => ({
  id: graphItem.registerIndex,
  registerIndex: String(graphItem.registerIndex),
  registerId: graphItem.registerSpecificationId,
  registerSpecificationGroupId: graphItem.registerSpecificationGroupId,
  name: graphItem.registerName!,
  valueBitMask: graphItem.bitMask,
  synthetic: true,

  // Required, bogus values
  groupName: '',
  groupItemOrder: 0,
  isProjection: false,
  isReadOnly: true,
  valueNames: [],
  valueType: graphItem.bitMask ? RegisterValueType.Flags : RegisterValueType.Number,
});

/*
 * Map diagrams to data points (registers) functions
 */
const mapDiagramItemToRegisterDataSeries = (
  registerDataSeriesArray: RegisterDataSeries[],
  diagramItem: DiagramItem,
): RegisterDataSeries => {
  const value = registerDataSeriesArray
    .find((registerDataSeries) => diagramItemMatchesRegister(diagramItem, registerDataSeries))
    ?? createSyntheticRegisterDataSeries(diagramItem);
  return {
    ...value,
    contextType: diagramItem.contextType,
    visualisationType: diagramItem.visualisationType,
    resolutionUnits: diagramItem.resolutionUnits,
    desiredAggregation: diagramItem.aggregationMethod,
  };
};

// Create a separate data point for every valueName because
// they should be separately selectable
const expandFlags = (registerDataSeriesArray: RegisterDataSeries[]): RegisterDataSeries[] =>
  flatten(registerDataSeriesArray.map((registerDataSeries) => {
    if (registerDataSeries.valueType === RegisterValueType.Flags) {
      return registerDataSeries.valueNames.map((valueName): RegisterDataSeries => ({
        ...registerDataSeries,
        valueBitMask: valueName.value as BitMask16,
      }));
    }
    return [registerDataSeries];
  }));

// Placeholders stored while a register had not loaded become the register once it has;
// the same graphs come back when nothing changed.
export const resolveSyntheticSeries = (
  graphs: SelectedRegisterDataSeriesGraph[],
  registerDataSeriesArray: RegisterDataSeries[],
): SelectedRegisterDataSeriesGraph[] => {
  if (
    !graphs.some(({ registerDataSeriesArray: series }) => series.some(({ synthetic }) => synthetic))
  ) {
    return graphs;
  }
  const expandedDataPoints = expandFlags(registerDataSeriesArray);
  let resolved = false;
  const result = graphs.map((graph) => ({
    ...graph,
    registerDataSeriesArray: graph.registerDataSeriesArray.map((series) => {
      const match = series.synthetic
        && expandedDataPoints.find((register) =>
          diagramItemMatchesRegister(mapDataPoint(series), register)
        );
      if (!match) {
        return series;
      }
      resolved = true;
      return { ...series, ...match, synthetic: undefined };
    }),
  }));
  return resolved ? result : graphs;
};

export const mapDiagramsToSelectedRegisterDataSeriesGraph = (
  registerDataSeriesArray: RegisterDataSeries[],
  diagrams?: Diagram[],
): SelectedRegisterDataSeriesGraph[] => {
  const expandedDataPoints = expandFlags(registerDataSeriesArray);
  return diagrams
    ?.map((diagram): SelectedRegisterDataSeriesGraph => ({
      registerDataSeriesArray: diagram.graphItems.map(
        (diagramItem) => mapDiagramItemToRegisterDataSeries(expandedDataPoints, diagramItem),
      ),
    }))
    || emptySelectedRegisterDataSeriesGraphs;
};

/*
 * API request mapping functions
 */
const mapDiagramItem = (diagramItem: DiagramItem): GraphProfileRequestGraphItem => ({
  registerSpecificationId: diagramItem.registerSpecificationId,
  registerSpecificationGroupId: diagramItem.registerSpecificationGroupId,
  graphItemContextType: diagramItem.contextType,
  graphItemVisualisationType: diagramItem.visualisationType,
  aggregationMethod: diagramItem.aggregationMethod,
  flag: diagramItem.bitMask,
});

const mapDiagramToGraph = (diagram: Diagram): GraphProfileRequestGraph => ({
  graphItems: diagram.graphItems
    .map((item) => mapDiagramItem(item)),
});

const mapDiagramsToGraphs = (diagrams: Diagram[]) => (
  diagrams.map((diagram) => mapDiagramToGraph(diagram))
);

export const createRequestFromGraphProfile = (
  partialGraphProfile: PartialGraphProfile,
): GraphProfilesRequest => ({
  isGlobal: false,
  installationProfileId: partialGraphProfile.deviceTypeId,
  graphProfileContextType: partialGraphProfile.graphProfileContextType,
  name: partialGraphProfile.name,
  graphs: mapDiagramsToGraphs(partialGraphProfile.diagrams),
});

/*
 * API response mapping functions
 */
const mapGraphItem = (graphItem: GraphProfilesResponseGraphItem): DiagramItem => ({
  registerSpecificationId: graphItem.registerSpecificationId,
  registerSpecificationGroupId: graphItem.registerSpecificationGroupId,
  registerIndex: graphItem.registerIndex,
  registerName: graphItem.registerName,
  bitMask: graphItem.flag as BitMask16,
  contextType: graphItem.graphItemContextType ?? GraphItemContextType.DataPoints,
  visualisationType: graphItem.graphItemVisualisationType ?? GraphItemVisualisationType.Line,
  resolutionUnits: graphItem.resolutionUnits,
  aggregationMethod: graphItem.aggregationMethod,
});

const mapGraphToDiagram = (graph: GraphProfilesResponseGraph): Diagram => ({
  graphItems: graph.graphItems
    .map((item) => mapGraphItem(item))
    .sort((a, b) => a.registerIndex - b.registerIndex),
});

const mapGraphsToDiagrams = (graphs: GraphProfilesResponseGraph[]): Diagram[] => (
  graphs.map((graph) => mapGraphToDiagram(graph))
);

export const createGraphProfileFromResponse = (
  response: GraphProfilesResponse,
): GraphProfile => ({
  id: response.id,
  isGlobal: response.isGlobal,
  graphProfileContextType: response.graphProfileContextType,
  deviceTypeId: response.installationProfileId,
  name: response.name,
  diagrams: mapGraphsToDiagrams(response.graphs),
});

export const mapToDiagramItemDifferenceIdentifier = (
  diagramItem: DiagramItem,
) =>
  `${diagramItem.registerSpecificationGroupId ?? NotAvailable}:${diagramItem.registerIndex}:${
    diagramItem.visualisationType ?? GraphItemVisualisationType.Line
  }:${diagramItem.aggregationMethod ?? DataHistoryAggregation.None}:${
    diagramItem.contextType ?? GraphItemContextType.DataPoints
  }`;

export const mapToDataPointDifferenceIdentifier = (
  registerDataSeries: RegisterDataSeries,
) =>
  `${
    registerDataSeries.registerSpecificationGroupId ?? NotAvailable
  }:${registerDataSeries.registerIndex}:${
    registerDataSeries.visualisationType ?? GraphItemVisualisationType.Line
  }:${registerDataSeries.desiredAggregation ?? DataHistoryAggregation.None}:${
    registerDataSeries.contextType ?? GraphItemContextType.DataPoints
  }`;
